import { BrowserRouter, HashRouter } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { validateSession } from "./api/service";
import { loginSuccess, logout, setLoading } from "./store/slice/authSlice";
import socketService from "./services/socketService";
import { unlockNotificationSound, handleTaskStatusChanged } from "./services/realtimeNotificationHandler";
import { showLeadBrowserNotification } from "./services/browserNotificationService";
import AppRoutes from "./routes/AppRoutes";
import { isPublicAppPath } from "./pages/public/publicSite";
import { assetPath } from "./utils/assetPath";
import FixProfileImageModal from "./components/shared/modal/FixProfileImageModal";
import RealtimeAlertsProvider from "./components/shared/RealtimeAlertsProvider";
import BrowserNotificationPrompt from "./components/shared/BrowserNotificationPrompt";
import PwaInstallBanner from "./components/shared/PwaInstallBanner";
import NetworkReconnectToast from "./components/shared/NetworkReconnectToast";

const isDesktop = typeof window !== "undefined" && window.desktop;
const Router = isDesktop ? HashRouter : BrowserRouter;

function App() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { loading, user } = useSelector((state) => state.auth); // Get loading and isAuthenticated state from Redux
  const [isAuthChecked, setIsAuthChecked] = useState(false); // Track if auth check is complete
  const [showFixProfileModal, setShowFixProfileModal] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const path = window.location.pathname;
      const isPublicPage =
        isPublicAppPath(path) ||
        path.startsWith("/auth/") ||
        path.includes("/portal/login");
      const token = localStorage.getItem("token");

      if (!token) {
        dispatch(setLoading(false));
        setIsAuthChecked(true);
        return;
      }

      dispatch(setLoading(true)); // Set loading to true
      try {
        const { user } = await validateSession();

        dispatch(
          loginSuccess({
            user: user,
            isProfileComplete: user?.isProfileComplete || false,
            companyId: user?.company,
          })
        );

        // A saved token on the public site or sign-in page means the last login
        // already worked. Open the app so the check-in state is visible.
        if (
          user?.role !== "client" &&
          (path === "/home" || path.startsWith("/auth/signin"))
        ) {
          window.location.replace("/");
          return;
        }

        // Check if profile image is accessible (don't block the main app load)
        if (user?.profileImage && typeof user.profileImage === "string" && user.profileImage.startsWith("http")) {
          fetch(user.profileImage, { 
            method: "HEAD",
            cache: 'no-cache'
          }).then(imgResponse => {
            if (imgResponse.status === 403) {
              setShowFixProfileModal(true);
            }
          }).catch(err => {
            // Only log if it's not a CORS/Network error which is common for HEAD requests to some external storages
            if (err.name !== 'TypeError') {
              console.error("Error checking profile image:", err);
            }
          });
        }

        // Initialize socket connection after successful authentication
        const token = localStorage.getItem("token");
        if (token) {
          socketService.connect(token);

          if (user?.company) {
            socketService.joinRoom(`company_${user.company}`);
          }

          // Global lead notification with sound + desktop alert
          socketService.onNewLeadReceived((data) => {
                try {
                    const audio = new Audio('/src/assets/audio/new-notification-017-352293.mp3');
                    audio.play();
                } catch (e) {
                    console.error("Failed to play notification sound:", e);
                }

                try {
                    toast.success(`🎯 New Lead: ${data.leadName || 'Someone'} interested in ${data.campaignName}`, {
                        duration: 6000,
                        icon: '🚀'
                    });
                } catch (e) {
                    console.error("Failed to show toast notification:", e);
                }

                showLeadBrowserNotification(data);

                try {
                    queryClient.invalidateQueries({ queryKey: ["leads"] });
                    queryClient.invalidateQueries({ queryKey: ["leadStats"] });
                    queryClient.invalidateQueries({ queryKey: ["campaigns"] });
                } catch (err) {
                    console.error("[Socket] Refetch failed:", err);
                }
            });
        }
      } catch (error) {
        const message = typeof error === "string" ? error : error?.message || "";
        const authFailed = /token|denied|unauthorized|expired|user not found/i.test(message);
        if (authFailed) {
          dispatch(logout());
          socketService.disconnect();
        }
      } finally {
        dispatch(setLoading(false)); // Set loading to false

        setIsAuthChecked(true); // Mark auth check as complete
      }
    };
    checkAuth();
  }, [dispatch]);

  // Real-time task status updates (toast/sound/bell live in GlobalNudges)
  useEffect(() => {
    if (!isAuthChecked) return;

    const handleTaskStatusChangedEvent = (data) => {
      handleTaskStatusChanged(data, queryClient);
    };

    socketService.onTaskStatusChange(handleTaskStatusChangedEvent);

    return () => {
      socketService.offTaskStatusChange(handleTaskStatusChangedEvent);
    };
  }, [isAuthChecked, queryClient]);

  // Unlock notification audio after first user interaction (browser autoplay policy)
  useEffect(() => {
    const unlock = () => unlockNotificationSound();
    document.addEventListener("click", unlock, { once: true });
    document.addEventListener("keydown", unlock, { once: true });
    return () => {
      document.removeEventListener("click", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, []);

  const publicPath =
    typeof window !== "undefined" && isPublicAppPath(window.location.pathname);
  const isBooting = !publicPath && (loading || !isAuthChecked);

  return (
    <>
      {isBooting ? (
        <div className="h-screen w-full flexCenter">
          <img src={assetPath("icons/loading.svg")} alt="" />
        </div>
      ) : (
        <>
          <Router>
            <AppRoutes />
            {!publicPath && user && <PwaInstallBanner />}
            {!publicPath && user && <BrowserNotificationPrompt />}
          </Router>

          {!publicPath && <RealtimeAlertsProvider />}

          {showFixProfileModal && (
            <FixProfileImageModal
              isOpen={showFixProfileModal}
              onClose={() => setShowFixProfileModal(false)}
              user={user}
            />
          )}
        </>
      )}

      <Toaster
        position="top-right"
        containerStyle={{ zIndex: 2147483647 }}
        toastOptions={{
          duration: 3000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            style: {
              background: "#10B981",
              color: "#fff",
            },
          },
          error: {
            duration: 4000,
            style: {
              background: "#EF4444",
              color: "#fff",
            },
          },
        }}
      />

      <NetworkReconnectToast />
    </>
  );
}

export default App;
